import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  TextNode,
} from 'lexical';
import { $removeEmptyNoWrapNodesByKeys } from 'modules/Lexical/nodes/NoWrapNode/utils';
import { useEffect } from 'react';
import { NoWrapNode, $isNoWrapTextNode } from '../nodes/NoWrapNode';
import { $toggleNoWrap } from './BasePropsPlugin/utils';

export const TEXT_NOWRAP_COMMAND = createCommand();

export type NoWrapProps = {
  textNoWrap: boolean;
};

type Props = {
  onChange: (props: NoWrapProps) => void;
};

function $getTextNoWrapFromSelection(): boolean {
  const selection = $getSelection();
  if (selection && $isRangeSelection(selection)) {
    const textNodes = selection.getNodes().flatMap((node) => {
      if ($isTextNode(node)) {
        return [node];
      }
      if ($isElementNode(node)) {
        return node.getAllTextNodes();
      }

      return [];
    });

    return textNodes.length > 0 && textNodes.every($isNoWrapTextNode);
  }

  return false;
}

export const NoWrapPlugin: React.FunctionComponent<Props> = (props: Props) => {
  const { onChange } = props;
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const readEditorStateAndNotifyChange = (): void => onChange({
      textNoWrap: $getTextNoWrapFromSelection(),
    });
    editor.getEditorState().read(readEditorStateAndNotifyChange);

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(readEditorStateAndNotifyChange);
    });
  }, [editor, onChange]);

  // Mutation listeners to clean up empty NoWrapNodes only when relevant nodes change
  useEffect(() => {
    const runCleanupIfNeeded = (mutations: Map<string, string>): void => {
      const nodeKeysToCheck = new Set<string>();

      for (const [nodeKey, type] of mutations) {
        if (type === 'destroyed' || type === 'updated') {
          nodeKeysToCheck.add(nodeKey);
        }
      }

      if (nodeKeysToCheck.size > 0) {
        editor.update(() => {
          $removeEmptyNoWrapNodesByKeys(nodeKeysToCheck);
        });
      }
    };

    return mergeRegister(
      editor.registerMutationListener(TextNode, runCleanupIfNeeded, { skipInitialization: true }),
      editor.registerMutationListener(NoWrapNode, runCleanupIfNeeded, { skipInitialization: true }),

      editor.registerCommand(
        TEXT_NOWRAP_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return false;
          }

          $toggleNoWrap(selection);

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  return null;
};
